if (!customElements.get('cross-sell-cart')) {
  customElements.define(
    'cross-sell-cart',
    class CrossSellCart extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.cross-sell-cart__button');
        this.errorElement = this.querySelector('.cross-sell-cart__error');
        this.spinner = this.querySelector('.loading__spinner');

        if (this.button) {
          this.button.addEventListener('click', this.onAddToCart.bind(this));
        }
      }

      connectedCallback() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'cross-sell-cart') return;
            this.refreshSelf();
          });
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      async refreshSelf() {
        const url = `${window.routes.cart_url}?sections=${this.dataset.sectionId}`;
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          const sections = await response.json();
          const html = sections[this.dataset.sectionId];
          if (!html) return;
          const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
          const source = parsedHtml.querySelector('cross-sell-cart');
          if (source) this.replaceWith(source);
        } catch (error) {
          console.error(error);
        }
      }

      async onAddToCart() {
        if (this.button.hasAttribute('disabled') || this.button.getAttribute('aria-disabled') === 'true') return;

        const variantId = this.dataset.variantId;
        if (!variantId) return;

        this.setLoading(true);
        this.hideError();

        try {
          const discountCode = this.dataset.discountCode;
          if (discountCode) {
            await this.applyDiscount(discountCode);
          }

          await this.addToCart(variantId);
          await this.refreshSections();
        } catch (error) {
          console.error(error);
          this.showError(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'Errore');
        } finally {
          this.setLoading(false);
        }
      }

      applyDiscount(code) {
        return fetch(`/discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          credentials: 'same-origin',
        }).catch((error) => {
          console.error('Cross-sell cart: unable to apply discount code', error);
        });
      }

      async addToCart(variantId) {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
          }),
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }
        return data;
      }

      async refreshSections() {
        const sectionsToRender = this.getSectionsToRender();
        const requestIds = sectionsToRender.map((section) => section.requestId);
        const url = `${window.routes.cart_url}?sections=${requestIds.join(',')}`;

        const response = await fetch(url, { credentials: 'same-origin' });
        const sections = await response.json();

        sectionsToRender.forEach((section) => {
          const html = sections[section.requestId];
          if (!html) return;

          const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
          const source = parsedHtml.querySelector(section.selector);
          if (!source) return;

          if (section.replaceSelf) {
            this.replaceWith(source);
            return;
          }

          const targetRoot = document.getElementById(section.targetId);
          if (!targetRoot) return;
          const target = targetRoot.querySelector(section.selector) || targetRoot;
          target.innerHTML = source.innerHTML;
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cross-sell-cart' });
        }
      }

      getSectionsToRender() {
        const mainCartItems = document.getElementById('main-cart-items');
        const mainCartFooter = document.getElementById('main-cart-footer');

        const sections = [
          { requestId: 'cart-icon-bubble', targetId: 'cart-icon-bubble', selector: '.shopify-section' },
          { requestId: 'cart-live-region-text', targetId: 'cart-live-region-text', selector: '.shopify-section' },
          { requestId: this.dataset.sectionId, selector: 'cross-sell-cart', replaceSelf: true },
        ];

        if (mainCartItems) {
          sections.push({ requestId: mainCartItems.dataset.id, targetId: 'main-cart-items', selector: '.js-contents' });
        }
        if (mainCartFooter) {
          sections.push({ requestId: mainCartFooter.dataset.id, targetId: 'main-cart-footer', selector: '.js-contents' });
        }

        return sections;
      }

      setLoading(isLoading) {
        this.button.toggleAttribute('disabled', isLoading);
        this.button.setAttribute('aria-disabled', String(isLoading));
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.hidden = true;
      }
    }
  );
}
